import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import LoginIcon from '@mui/icons-material/Login';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import * as userApi from '../../../../api/user';
import * as patreonApi from '../../../../api/patreon';
import { saveNextUrl } from '../../domain/authNavigation';
import { theme } from '../../../../theme';

type ProviderType = 'subscriber' | 'patreon' | 'direct';

type AuthChoicePanelProps = {
  nextPath: string;
  title: string;
  description: string;
  eyebrow?: string;
  compact?: boolean;
};

export default function AuthChoicePanel({
  nextPath,
  title,
  description,
  eyebrow = 'Authentication',
  compact = false,
}: AuthChoicePanelProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [busyProvider, setBusyProvider] = useState<ProviderType | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const currentPath = useMemo(
    () => `${location.pathname}${location.search}${location.hash}`,
    [location.hash, location.pathname, location.search]
  );

  const startAuth = async (provider: ProviderType) => {
    setBusyProvider(provider);
    setErrorMsg(null);
    saveNextUrl(nextPath);

    try {
      if (provider === 'subscriber') {
        const response = await userApi.getSubscriberAuthUrl();
        if (response.data?.url) {
          window.location.replace(response.data.url);
          return;
        }
        throw new Error('Could not start SubscribeStar authentication.');
      }

      if (provider === 'patreon') {
        const response = await patreonApi.getPatreonAuthUrl();
        if (response.data?.url) {
          window.location.replace(response.data.url);
          return;
        }
        throw new Error('Could not start Patreon authentication.');
      }

      navigate(`/accessLogin?next=${encodeURIComponent(nextPath)}`, { replace: false });
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Could not start authentication. Please try again.';

      setErrorMsg(message);
      setBusyProvider(null);
    }
  };

  return (
    <Box
      sx={{
        minHeight: compact ? 'auto' : '100vh',
        background:
          'radial-gradient(circle at top left, rgba(209, 76, 255, 0.22), transparent 35%), linear-gradient(180deg, #18131d 0%, #120f16 100%)',
        py: compact ? 0 : { xs: 4, md: 8 },
        px: 2,
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={0}
          sx={{
            overflow: 'hidden',
            borderRadius: 4,
            border: `1px solid ${theme.palette.divider}`,
            background: 'linear-gradient(145deg, rgba(42, 36, 48, 0.98), rgba(25, 21, 30, 0.98))',
            boxShadow: '0 24px 60px rgba(0, 0, 0, 0.35)',
          }}
        >
          <Box
            sx={{
              px: { xs: 3, md: 5 },
              py: { xs: 4, md: 5 },
              borderBottom: `1px solid ${theme.palette.divider}`,
              background: 'linear-gradient(180deg, rgba(209, 76, 255, 0.14), rgba(209, 76, 255, 0))',
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '16px',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'rgba(209, 76, 255, 0.18)',
                  color: theme.palette.primary.main,
                }}
              >
                <LockOutlinedIcon />
              </Box>
              <Chip
                label={eyebrow}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.06)',
                  color: theme.palette.text.secondary,
                  fontWeight: 600,
                  letterSpacing: 0.4,
                }}
              />
            </Stack>

            <Typography variant="h3" sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, fontWeight: 800, mb: 1.5 }}>
              {title}
            </Typography>
            <Typography sx={{ color: theme.palette.text.secondary, maxWidth: 680 }}>
              {description}
            </Typography>
          </Box>

          <Box sx={{ px: { xs: 3, md: 5 }, py: { xs: 3, md: 4 } }}>
            {errorMsg && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {errorMsg}
              </Alert>
            )}

            <Stack spacing={2}>
              <Button
                fullWidth
                size="large"
                variant="contained"
                startIcon={busyProvider === 'subscriber' ? <CircularProgress size={18} color="inherit" /> : <AlternateEmailIcon />}
                endIcon={busyProvider === 'subscriber' ? undefined : <OpenInNewIcon />}
                disabled={busyProvider !== null}
                onClick={() => void startAuth('subscriber')}
                sx={{
                  justifyContent: 'space-between',
                  px: 2.5,
                  py: 1.7,
                  borderRadius: 3,
                  bgcolor: theme.palette.primary.main,
                  '&:hover': { bgcolor: theme.palette.primary.main, opacity: 0.92 },
                }}
              >
                Continue with SubscribeStar
              </Button>

              <Button
                fullWidth
                size="large"
                variant="outlined"
                startIcon={busyProvider === 'patreon' ? <CircularProgress size={18} color="inherit" /> : <FavoriteBorderIcon />}
                endIcon={busyProvider === 'patreon' ? undefined : <OpenInNewIcon />}
                disabled={busyProvider !== null}
                onClick={() => void startAuth('patreon')}
                sx={{
                  justifyContent: 'space-between',
                  px: 2.5,
                  py: 1.7,
                  borderRadius: 3,
                  borderColor: 'rgba(255,255,255,0.18)',
                  color: theme.palette.text.primary,
                }}
              >
                Continue with Patreon
              </Button>

              <Button
                fullWidth
                size="large"
                variant="text"
                startIcon={busyProvider === 'direct' ? <CircularProgress size={18} color="inherit" /> : <LoginIcon />}
                disabled={busyProvider !== null}
                onClick={() => void startAuth('direct')}
                sx={{
                  justifyContent: 'center',
                  py: 1.4,
                  borderRadius: 3,
                  color: theme.palette.text.secondary,
                }}
              >
                Use direct reader login
              </Button>
            </Stack>

            {!compact && (
              <Typography sx={{ mt: 3, color: theme.palette.text.secondary, fontSize: 13 }}>
                You will return to <strong>{nextPath}</strong> after authentication.
              </Typography>
            )}

            {currentPath !== '/login' && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
                <Button
                  variant="text"
                  startIcon={<ArrowBackOutlinedIcon />}
                  onClick={() => navigate(-1)}
                  sx={{ color: theme.palette.text.secondary }}
                >
                  Back
                </Button>
                <Button
                  variant="text"
                  onClick={() => navigate(`/login?next=${encodeURIComponent(nextPath)}`)}
                  sx={{ color: theme.palette.text.secondary }}
                >
                  Open the full sign-in page
                </Button>
              </Stack>
            )}
            {currentPath === '/login' && (
              <Button
                variant="text"
                startIcon={<ArrowBackOutlinedIcon />}
                onClick={() => navigate(-1)}
                sx={{ mt: 2, color: theme.palette.text.secondary }}
              >
                Back
              </Button>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
